/* Copyright (c)2022 Sky Co., LTD. All rights reserved. */

/// <summary>
/// アプリケーション情報
/// </summary>
export class AppInfo {
    /// <summary>
    /// アプリケーション名
    /// </summary>
    trackName = "";

    /// <summary>
    /// 会社名・販売元
    /// </summary>
    sellerName = "";

    /// <summary>
    /// バージョン情報
    /// </summary>
    version = "";

    /// <summary>
    /// 最終バージョンアップ日
    /// </summary>
    currentVersionReleaseDate = "";

    /// <summary>
    /// リリースノート
    /// </summary>
    releaseNotes = "";

    /// <summary>
    /// ファイルサイズ
    /// </summary>
    fileSizeBytes = "";

    /// <summary>
    /// カテゴリ
    /// </summary>
    genres = "";

    /// <summary>
    /// 互換性
    /// </summary>
    isExistsiPhone = false;     // iPhone
    isExistsiPad = false;       // iPad
    isExistsiPodTouch = false;  // iPodTouch
    minimumOsVersion = "";      // 最小バージョン

    /// <summary>
    /// 言語
    /// </summary>
    language = "";

    /// <summary>
    /// 年齢制限
    /// </summary>
    contentAdvisoryRating = "";

    /// <summary>
    /// アイコン(60×60)
    /// </summary>
    artworkUrl60 = "";

    /// <summary>
    /// アイコン(100×100)
    /// </summary>
    artworkUrl100 = "";

    /// <summary>
    /// 説明
    /// </summary>
    description = "";

    /// <summary>
    /// 識別情報
    /// </summary>
    id = 0;

    /// 以下、システム固有の設定

    /// <summary>
    /// VPPアプリかどうか
    /// </summary>
    isVpp = false;

    /// <summary>
    /// アップデートが必要かどうか
    /// </summary>
    hasUpdateAvailable = false;

    /// <summary>
    /// インストール済みかどうか
    /// </summary>
    isInstalled = false;

    /// <summary>
    /// インストール・アップデート依頼済み（UI情報）
    /// </summary>
    isRequestInstallUpdate = false;

    /// <summary>
    /// 設定ID
    /// </summary>
    settingID = 0;

    /// <summary>
    /// 説明
    /// </summary>
    explanation = "";
}

/// <summary>
/// フォルダ情報
/// </summary>
export class FolderInfo {
    /// <summary>
    /// フォルダーID
    /// </summary>
    catalogFolderID = 0;

    /// <summary>
    /// 親のフォルダーID
    /// </summary>
    parentCatalogFolderID = 0;

    /// <summary>
    /// フォルダ名
    /// </summary>
    catalogFolderName = "";

    /// <summary>
    /// 表示順（オンプレのみ指定。M1は全て0が入っている）
    /// </summary>
    catalogFolderDisplayOrder = 0;

    /// <summary>
    /// フォルダのデザイン
    /// </summary>
    catalogFolderDesign = 0;

    /// <summary>
    /// フォルダの中に含まれるアプリ数(サブフォルダ含む)
    /// </summary>
    appCountInFolder = 0;

    /// <summary>
    /// フォルダの中に含まれる未インストールのアプリ数(サブフォルダ含む)
    /// </summary>
    appCountInFolderForNotInstall = 0;
}

/// <summary>
/// フォルダ情報（UI用に扱いやすいようにしたデータクラス）
/// </summary>
export class FolderData {

    /// <summary>
    /// フォルダーID
    /// </summary>
    catalogFolderID = 0;

    /// <summary>
    /// フォルダ名
    /// </summary>
    catalogFolderName = "";

    /// <summary>
    /// 表示順（オンプレのみ指定。M1は全て0が入っている）
    /// </summary>
    catalogFolderDisplayOrder = 0;

    /// <summary>
    /// フォルダのデザイン
    /// </summary>
    catalogFolderDesign = 0;

    /// <summary>
    /// アプリ情報(自分のフォルダのみ)
    /// </summary>
    installApp = new Array<AppInfo>(); 

    /// <summary>
    /// フォルダの中に含まれるアプリ情報(サブフォルダ含む)
    /// </summary>
    appIDInFolder = new Array<AppInfo>(); 

    /// <summary>
    /// サブフォルダ情報
    /// </summary>
    arSubFolderInfo = new Array<FolderData>();

    /// <summary>
    /// 初期化
    /// </summary>
    public SetDefault(): void {
        this.catalogFolderID = 0;
        this.catalogFolderName = "";
        this.catalogFolderDisplayOrder = 0;
        this.catalogFolderDesign = 0;
        this.installApp = new Array<AppInfo>(); 
        this.appIDInFolder = new Array<AppInfo>();
        this.arSubFolderInfo = new Array<FolderData>();
    }

    /// <summary>
    /// データベースの情報をデータクラスに変換します
    /// @param [in] arItemList : データベースのデータ
    /// </summary>
    public Load(
        folderInfoAll: Array<FolderInfo>,
        folderSettingAll: Array<FolderSetting>,
        installAllApp: Array<AppInfo>
    ): void {
        // 各編集を初期化
        this.SetDefault();

        // Key:SettingID Value:アプリ情報
        let mapApp = new Map<number, AppInfo>();

        // アプリ情報(自分のフォルダのみ) + サブフォルダを含むアプリ情報作成
        for (let nCnt = 0; nCnt < installAllApp.length; ++nCnt) {
            // アプリ一覧のmap作成
            mapApp.set(installAllApp[nCnt].settingID, installAllApp[nCnt]);

            let bExist = false;
            for (let i = 0; i < folderSettingAll.length; ++i) {

                if (folderSettingAll[i].settingID.includes(installAllApp[nCnt].settingID)) {
                    bExist = true;
                    break;
                }
            }
            if (!bExist) {
                this.installApp.push(installAllApp[nCnt])
            }
        }
        // 親だけは、どのフォルダにも属さないアプリ＋配下アプリの合計になる
        this.appIDInFolder = Array.from(new Set(this.installApp.concat(FolderDataUtil.GetInstallAppIDInFolder(folderInfoAll, folderSettingAll, 0, mapApp))));

        // アイテム情報のマップ（Key:親フォルダID Value:フォルダ情報）
        const itemParentMap = new Map<number, Array<FolderInfo>>();     
        for (let nCnt = 0; nCnt < folderInfoAll.length; ++nCnt) {
            let parent = itemParentMap.get(folderInfoAll[nCnt].parentCatalogFolderID);
            if (undefined == parent) {
                parent = new Array<FolderInfo>();
            }
            parent.push(folderInfoAll[nCnt])
            itemParentMap.set(folderInfoAll[nCnt].parentCatalogFolderID, parent);
        }

        // サブフォルダ作成
        const child = itemParentMap.get(0);
        if (undefined == child) {
            return;
        }
        this.arSubFolderInfo = this.create(child, itemParentMap, folderInfoAll, folderSettingAll, installAllApp, mapApp);
    }

    /**
     * フォルダ情報作成
     * @return なし
     */
    public create(infoList: Array<FolderInfo>, itemParentMap: Map<number, Array<FolderInfo>>, folderInfoAll: Array<FolderInfo>, folderSettingAll: Array<FolderSetting>, installAllApp: Array<AppInfo>, mapApp : Map<number, AppInfo>): Array<FolderData> {
        let ret = new Array<FolderData>();

        for (let nCnt = 0; nCnt < infoList.length; ++nCnt) {
            let item = new FolderData();

            item.catalogFolderID = infoList[nCnt].catalogFolderID;
            item.catalogFolderName = infoList[nCnt].catalogFolderName;
            item.catalogFolderDisplayOrder = infoList[nCnt].catalogFolderDisplayOrder;
            item.catalogFolderDesign = infoList[nCnt].catalogFolderDesign;
            item.appIDInFolder = FolderDataUtil.GetInstallAppIDInFolder(folderInfoAll, folderSettingAll, infoList[nCnt].catalogFolderID, mapApp);

            let settingData = folderSettingAll.find(x => x.catalogFolderID == infoList[nCnt].catalogFolderID);
            if (undefined != settingData) {
                item.installApp = installAllApp.filter(x => settingData?.settingID.includes(x.settingID));
            }

            let childinfoList = itemParentMap.get(infoList[nCnt].catalogFolderID);
            if (undefined != childinfoList) {
                item.arSubFolderInfo = this.create(childinfoList, itemParentMap, folderInfoAll, folderSettingAll, installAllApp, mapApp);
            }

            ret.push(item);
        }
        return ret;
    }

    /**
     * 指定されたアイテムIDの親アイテム情報を返します
     * @param[in] nItemID : アイテムID
     * @return undefined:見つからなかった undefined以外：アイテムID
     */
    public GetParentItemData(nCatalogFolderID: number): FolderData | undefined {
        if (this.catalogFolderID == nCatalogFolderID) {
            // 自分は親ではない
            return undefined;
        }

        for (let i = 0; i < this.arSubFolderInfo.length; i++) {
            if (this.arSubFolderInfo[i].catalogFolderID == nCatalogFolderID) {
                // 自分の子
                return this;
            }

            const ret = this.arSubFolderInfo[i].GetParentItemData(nCatalogFolderID);
            if (undefined != ret) {
                // 孫以上
                return ret;
            }
        }
        return undefined;
    }
}

export class FolderDataUtil {
    /**
     * @brief   フォルダの中(サブフォルダも含む)に含まれるアプリ数を返します。
     * @param   チェック対象のフォルダID
     * @return  アプリ数
     */
    public static GetInstallAppIDInFolder(folderInfoAll: Array<FolderInfo>, folderSettingAll: Array<FolderSetting>, catalogFolderID: number, mapApp: Map<number, AppInfo>): Array<AppInfo> {
        let appIDInFolder: Array<AppInfo> = new Array<AppInfo>();
        // まず自分のフォルダにアプリがあるかチェックする
        var folderSetting = folderSettingAll.find(x => x.catalogFolderID == catalogFolderID);
        if (undefined != folderSetting) {
            for (let i = 0; i < folderSetting.settingID.length; i++) {
                let appData = mapApp.get(folderSetting.settingID[i]);
                if (undefined != appData && !appIDInFolder.includes(appData)) {
                    appIDInFolder.push(appData);
                }
            }
        }

        // 次に自分のフォルダ内に含まれるフォルダの中にアプリが存在するかチェックする
        const folderInfo = folderInfoAll.filter(x => x.parentCatalogFolderID === catalogFolderID);
        for (let i = 0; i < folderInfo.length; i++) {
            appIDInFolder = Array.from(new Set(appIDInFolder.concat(FolderDataUtil.GetInstallAppIDInFolder(folderInfoAll, folderSettingAll, folderInfo[i].catalogFolderID, mapApp))));
        }

        return appIDInFolder;
    }
}

/// <summary>
/// フォルダに含まれるアプリ情報
/// </summary>
export class FolderSetting {
    /// <summary>
    /// フォルダーID
    /// </summary>
    catalogFolderID = 0;

    /// <summary>
    /// アプリの設定ID
    /// </summary>
    settingID = new Array<number>();
}

/// <summary>
/// アプリケーション
/// </summary>
export class AppInfoUtil {
    /**
     * 表示内容のコンバート（公開アプリ）
     * @param[in] jsonData : JSON.results[x]の内容
     */
    public static AppInfoToPublicAppJson(jsonData: any): AppInfo {
        const appInfo = new AppInfo();

        if (undefined == jsonData) {
            return appInfo;
        }

        // ID
        appInfo.id = this.NumberToJsonData(jsonData.trackId);

        // アプリ名
        appInfo.trackName = this.StringToJsonData(jsonData.trackName);

        // バージョン
        appInfo.version = this.StringToJsonData(jsonData.version);

        // 会社
        appInfo.sellerName = this.StringToJsonData(jsonData.sellerName);

        // 説明（改行を変換）
        appInfo.description = this.StringToJsonData(jsonData.description).replaceAll("\n", "<br />");

        // リリース日（現時刻からの差分）
        appInfo.currentVersionReleaseDate = this.ConvertToCurrentVersionReleaseDate(this.StringToJsonData(jsonData.currentVersionReleaseDate));

        // リリースノート（改行を変換）
        appInfo.releaseNotes = this.StringToJsonData(jsonData.releaseNotes).replaceAll("\n", "<br />");

        // ファイルサイズ（バイト⇒見やすい形式）
        appInfo.fileSizeBytes = this.ConvertToFileSizeBytes(this.StringToJsonData(jsonData.fileSizeBytes));

        // カテゴリ（一行にする）
        appInfo.genres = this.ConvertToCategory(jsonData.genres);

        // 互換性
        this.ConvertToSupported(jsonData.supportedDevices, appInfo);
        appInfo.minimumOsVersion = this.StringToJsonData(jsonData.minimumOsVersion);

        // 言語
        appInfo.language = this.ConvertToLanguage(this.StringToJsonData(jsonData.currency));

        // 年齢制限
        appInfo.contentAdvisoryRating = this.StringToJsonData(jsonData.contentAdvisoryRating);

        // アイコン
        appInfo.artworkUrl60 = this.StringToJsonData(jsonData.artworkUrl60);
        appInfo.artworkUrl100 = this.StringToJsonData(jsonData.artworkUrl100);

        return appInfo;
    }

    /**
     * 表示内容のコンバート（VPPアプリ）
     * @param[in] jsonData : JSON.results[x]の内容
     */
    public static AppInfoToVppAppJson(jsonData: any): AppInfo {
        const appInfo = new AppInfo();

        if (undefined == jsonData) {
            return appInfo;
        }

        // ID
        appInfo.id = this.NumberToJsonData(jsonData.id);

        // アプリ名
        appInfo.trackName = this.StringToJsonData(jsonData.name);

        // offers系
        if (undefined != jsonData.offers && undefined != jsonData.offers.length && 1 <= jsonData.offers.length) {
            // バージョン
            appInfo.version = this.StringToJsonData(jsonData.offers[0].version.display);

            // ファイルサイズ（バイト⇒見やすい形式）
            if (undefined != jsonData.offers[0].assets && undefined != jsonData.offers[0].assets.length && 1 <= jsonData.offers[0].assets.length) {
                appInfo.fileSizeBytes = this.ConvertToFileSizeBytes(this.StringToJsonData(jsonData.offers[0].assets[0].size));
            }
        }

        // softwareInfo系
        if (undefined != jsonData.softwareInfo) {
            // 会社
            appInfo.sellerName = this.StringToJsonData(jsonData.softwareInfo.seller);

            // 言語
            appInfo.language = this.StringToJsonData(jsonData.softwareInfo.languagesDisplayString);
        }

        // 説明（改行を変換）
        if (undefined != jsonData.description) {
            appInfo.description = this.StringToJsonData(jsonData.description.standard).replaceAll("\n", "<br />");
        }

        // リリース日（現時刻からの差分）
        appInfo.currentVersionReleaseDate = this.StringToJsonData(jsonData.latestVersionReleaseDate);

        // リリースノート（改行を変換）
        appInfo.releaseNotes = this.StringToJsonData(jsonData.whatsNew).replaceAll("\n", "<br />");

        // カテゴリ（一行にする）
        appInfo.genres = this.ConvertToCategory(jsonData.genreNames);

        // 互換性
        this.ConvertToSupported(jsonData.deviceFamilies, appInfo);
        appInfo.minimumOsVersion = this.StringToJsonData(jsonData.minimumOSVersion);

        // 年齢制限
        if (undefined != jsonData.contentRatingsBySystem && undefined != jsonData.contentRatingsBySystem.appsApple) {
            appInfo.contentAdvisoryRating = this.StringToJsonData(jsonData.contentRatingsBySystem.appsApple.name);
        }

        // アイコン
        if (undefined != jsonData.artwork) {
            appInfo.artworkUrl60 = this.StringToJsonData(jsonData.artwork.url);
            appInfo.artworkUrl60 = appInfo.artworkUrl60.replace("{w}", "60");
            appInfo.artworkUrl60 = appInfo.artworkUrl60.replace("{h}", "60");
            appInfo.artworkUrl60 = appInfo.artworkUrl60.replace("{f}", "jpg");
            appInfo.artworkUrl100 = this.StringToJsonData(jsonData.artwork.url);
            appInfo.artworkUrl100 = appInfo.artworkUrl100.replace("{w}", "100");
            appInfo.artworkUrl100 = appInfo.artworkUrl100.replace("{h}", "100");
            appInfo.artworkUrl100 = appInfo.artworkUrl100.replace("{f}", "jpg");
        }
        
        return appInfo;
    }

    /**
     * 表示内容のコンバート（カスタムAPP）
     * @param[in] info : カスタムAPPの内容
     */
    public static AppInfoToCustomAppInfo(info: AppInfo): AppInfo {
        if (undefined == info) {
            return info;
        }

        // 変換が必要なものだけ実施

        // ファイルサイズ
        if (undefined != info.fileSizeBytes) {
            info.fileSizeBytes = this.ConvertToFileSizeBytes(info.fileSizeBytes);
        }

        // 説明（改行を変換）
        if (undefined != info.description) {
            info.description = info.description.replaceAll("\n", "<br />");
        }

        // リリース日（現時刻からの差分）
        // 変換不要

        // リリースノート（改行を変換）
        info.releaseNotes = info.releaseNotes.replaceAll("\n", "<br />");

        // カテゴリ（一行にする）
        // 収集サーバーで変換済みのため変換不要

        // 互換性
        // 収集サーバーで変換済みのため変換不要

        // アイコン
        // 収集サーバーで変換済みのため変換不要

        return info;
    }

    /**
     * Jsonから文字列を抜き出す（未指定なら空文字）
     * @param[in] jsonData : JSON.results[x].xxxxxxxxの内容
     */
    private static StringToJsonData(jsonData: any): string {
        if (undefined == jsonData) {
            return "";
        }
        return jsonData;
    }

    /**
     * Jsonから文字列を抜き出す（未指定なら空文字）
     * @param[in] jsonData : JSON.results[x].xxxxxxxxの内容
     */
    private static NumberToJsonData(jsonData: any): number {
        if (undefined == jsonData) {
            return 0;
        }
        return jsonData;
    }

    /**
     * 表示内容のコンバート（互換性）
     * @param[in] jsonData : JSON.results[x].supportedDevicesの内容
     * @param[out]  appInfo : 結果格納先
     */
    private static ConvertToSupported(jsonData: any, appInfo: AppInfo): void {
        appInfo.isExistsiPhone = false;
        appInfo.isExistsiPad = false;
        appInfo.isExistsiPodTouch = false;
        if (undefined == jsonData || undefined == jsonData.length) {
            return ;
        }
        for(const device of jsonData)
        {
            const temp = this.StringToJsonData(device).toLowerCase();
            if (0 == temp.length) continue;

            // 機種名の前半で判断する。
            if (temp.startsWith("iphone")) {
                appInfo.isExistsiPhone = true;
            }
            else if (temp.startsWith("ipad")) {
                appInfo.isExistsiPad = true;
            }
            else if (temp.startsWith("ipodtouch") || temp.startsWith("ipod")) {
                appInfo.isExistsiPodTouch = true;
            }

            // 全種別揃ったら抜ける。
            if (appInfo.isExistsiPhone && appInfo.isExistsiPad && appInfo.isExistsiPodTouch) {
                break;
            }
        }
    }

    /**
     * 表示内容のコンバート（言語）
     * @param[in] jsonData : JSON.results[x].currencyの内容
     */
    private static ConvertToLanguage(jsonString: string): string {
        if ("JPY" == jsonString) {
            return "日本語";
        }
        else if ("US" == jsonString) {
            return "英語";
        }
        return "";
    }

    /**
     * 表示内容のコンバート（カテゴリ）
     * @param[in] jsonData : JSON.results[x].genresの内容
     */
    private static ConvertToCategory(jsonData: any): string {
        if (undefined == jsonData || undefined == jsonData.length) {
            return "";
        }
        let strCategories = "";
        for(const genres of jsonData)
        {
            const temp = this.StringToJsonData(genres);
            if (0 == temp.length) continue;
            if (0 < strCategories.length) {
                strCategories += ", ";
            }
            strCategories += temp;
        }
        return strCategories;
    }

    /**
     * 表示内容のコンバート（ファイルサイズ）
     * @param[in] jsonData : JSON.results[x].fileSizeBytesの内容
     */
    private static ConvertToFileSizeBytes(jsonString: string): string {
        const nValue = parseInt(jsonString);
        if (isNaN(nValue)) {
            return "";
        }
        // Byte単位を人間が見やすいように変換する。
        if ((1024 * 1024 * 1024) <= nValue) {
            // GB単位
            return ((Math.round(nValue / 1024 / 1024 / 1024) * 10) / 10) + "GB";
        }
        else if ((1024 * 1024) <= nValue) {
            // MB単位
            return ((Math.round(nValue / 1024 / 1024) * 10) / 10) + "MB";
        }
        else if (1024 < nValue) {
            // KB単位
            return ((Math.round(nValue / 1024) * 10) / 10) + "KB";
        }
        // B単位
        return nValue + "B";
    }

    /**
     * 表示内容のコンバート（日付）
     * @param[in] jsonData : JSON.results[x].currentVersionReleaseDateの内容
     */
    private static ConvertToCurrentVersionReleaseDate(jsonString: string): string {
        if ("" == jsonString) {
            return "";
        }

        const dtJson = new Date(jsonString);

        return dtJson.getFullYear() + "年" +(dtJson.getMonth() + 1) + "月" + dtJson.getDate() + "日";
    }
}

/* Copyright (c)2022 Sky Co., LTD. All rights reserved. */
